import { MessageCircle, Send } from "lucide-react";
import { FormEvent, useState } from "react";
import type { ChatChannel, PublicPlayer } from "@ma-soi/game-core";
import { SeatAvatar } from "./SeatAvatar";
import { errorMessage } from "../lib";

export interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  text: string;
  channel: ChatChannel;
}

export function ChatPanel({
  messages,
  channel,
  canSend,
  readOnlyReason,
  onSend,
  players = [],
}: {
  messages: ChatMessage[];
  channel: ChatChannel;
  canSend: boolean;
  readOnlyReason?: string;
  onSend: (text: string) => Promise<void>;
  players?: PublicPlayer[];
}) {
  const [value, setValue] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!value.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      await onSend(value);
      setValue("");
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setSending(false);
    }
  };
  const labels: Record<ChatChannel, string> = {
    lobby: "Chat phòng chờ",
    alive: "Chat",
    ghost: "Kênh người chết",
    wolves: "Kênh Sói",
  };
  return (
    <section className="surface-panel chat-panel">
      <h3 className="panel-title"><MessageCircle />{labels[channel]}</h3>
      <div className="chat-scroll">
        {messages.length === 0 && <p className="chat-empty">Chưa có tin nhắn.</p>}
        {messages.map((message) => {
          const player = players.find((candidate) => candidate.uid === message.senderId);
          return (
            <article key={message.id} className="chat-message">
              {player ? <SeatAvatar seat={player.seat} avatarId={player.avatarId} name={player.name} muted={!player.alive} /> : <SeatAvatar seat={99} name={message.senderName} />}
              <p><strong>{message.senderName}</strong><span>{message.text}</span></p>
            </article>
          );
        })}
      </div>
      <form onSubmit={submit} className="chat-form">
        {error && <p className="error-box chat-send-error" role="alert">{error}</p>}
        {!canSend && readOnlyReason && <p className="chat-readonly-label" role="status">{readOnlyReason}</p>}
        <input
          className="field-input"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          maxLength={300}
          placeholder={canSend ? "Nhập tin nhắn…" : readOnlyReason ?? "Bạn chỉ có thể lắng nghe…"}
          disabled={!canSend}
          aria-label="Tin nhắn"
        />
        <button className="icon-button" disabled={!canSend || sending || !value.trim()} aria-label="Gửi tin nhắn"><Send /></button>
      </form>
    </section>
  );
}
